import { RAM_JE_DATEI, medienSchluessel, ramPlan, type RamEintrag } from '@schnitt/shared';

/**
 * Die Vorschau-Dateien im Arbeitsspeicher (7.69s).
 *
 * Sascha, 10. September: „Also das Anhalten ist nicht schlecht. Aber kann man
 * das nicht irgendwie in den RAM vorladen?“
 *
 * Bis hierher lagen die Dateien im Zwischenspeicher der Seite (7.69n), und
 * das half beim *Holen*. Beim Abspielen holt das Videoelement trotzdem
 * stückweise nach -- über den Service Worker, aber eben Stück für Stück. Die
 * Befunde vom iPhone sagen, was das kostet: siebzehn Haltungen in zehn
 * Sekunden, und der Kopf lief mit 0,84-facher Geschwindigkeit.
 *
 * Liegt die Datei dagegen als Ganzes hier, bekommt das Element eine
 * `blob:`-Adresse -- und die liest es aus dem Speicher, ohne zu fragen. Kein
 * Nachladen, kein Warten, keine Haltungen.
 *
 * Was hineindarf und was dafür hinausfliegt, steht als Regel im gemeinsamen
 * Teil (`ramPlan`) und ist dort geprüft. Hier steht nur die Ausführung.
 */

interface Stueck {
  bytes: number;
  zuletzt: number;
  blob: string;
}

const vorrat = new Map<string, Stueck>();
/**
 * Wann eine Datei zuletzt gebraucht wurde.
 *
 * „In Gebrauch“ mit einem Zeitstempel statt mit einem Merkzettel, den jemand
 * wieder abräumen muss: ein Merkzettel, den niemand abräumt, füllt den
 * Speicher und lässt danach nichts Neues mehr hinein. Was seit einer halben
 * Minute niemand angefasst hat, darf hinaus.
 */
const gebraucht = new Map<string, number>();
const GILT_ALS_IN_GEBRAUCH = 30_000;
/**
 * Was nicht hineinpasst oder nicht zu holen war -- mit dem Zeitpunkt.
 *
 * Ohne dieses Gedächtnis fragt die Vorschau bei jedem Bild neu: eine Datei,
 * die zu gross ist, würde sechzigmal je Sekunde geholt und wieder verworfen.
 * Nach einer Minute darf es wieder jemand versuchen -- bis dahin kann sich
 * der Platz geändert haben.
 */
const geht_nicht = new Map<string, number>();
const NEUER_VERSUCH_NACH = 60_000;
/**
 * Wie oft schon geleert wurde -- der Griff gegen halb fertige Holvorgänge:
 * wer vor dem Leeren angefangen hat, trägt danach nichts mehr ein. Ohne das
 * blieben Blob-Adressen liegen, die niemand mehr freigibt.
 */
let leerungen = 0;

function inGebrauch(): Set<string> {
  const jetzt = Date.now();
  const raus = new Set<string>();
  for (const [url, wann] of gebraucht) {
    if (jetzt - wann < GILT_ALS_IN_GEBRAUCH) raus.add(url);
    else gebraucht.delete(url);
  }
  return raus;
}
/** Läuft schon ein Holen für diese Adresse? */
const laufend = new Map<string, Promise<string | null>>();

function eintraege(): RamEintrag[] {
  return [...vorrat.entries()].map(([schluessel, s]) => ({ schluessel, bytes: s.bytes, zuletzt: s.zuletzt }));
}

/**
 * Die Blob-Adresse zu einer Datei, wenn sie im Speicher liegt.
 *
 * Der Aufruf gilt als „gebraucht“: er hält den Eintrag frisch und schützt ihn
 * vor dem Hinauswerfen, solange das Element ihn benutzt.
 */
export function ausDemRam(url: string): string | null {
  const s = vorrat.get(url);
  if (!s) return null;
  s.zuletzt = Date.now();
  gebraucht.set(url, s.zuletzt);
  return s.blob;
}

/** Diese Datei wird nicht mehr gebraucht -- sie darf wieder hinausfliegen. */
export function nichtMehrGebraucht(url: string): void {
  gebraucht.delete(url);
}

/**
 * Diese Datei gibt es nicht mehr (7.83): Blob freigeben und alles vergessen,
 * was sie betrifft -- auch den Merker „geht nicht“, der sie nach einer
 * Minute wieder zum Versuch freigäbe.
 */
export function ausDemRamWerfen(url: string): void {
  // Nach Pfad, nicht nach Adresse: dieselbe Datei kann hier unter einer
  // älteren Marke liegen als in der Liste, die gerade offen ist.
  const schluessel = medienSchluessel(url);
  for (const gemerkt of [...vorrat.keys(), ...gebraucht.keys(), ...geht_nicht.keys()]) {
    if (medienSchluessel(gemerkt) !== schluessel) continue;
    const s = vorrat.get(gemerkt);
    if (s) {
      URL.revokeObjectURL(s.blob);
      vorrat.delete(gemerkt);
    }
    gebraucht.delete(gemerkt);
    geht_nicht.delete(gemerkt);
  }
}

/**
 * Eine Datei ganz in den Speicher holen. Läuft im Hintergrund; wer sie
 * braucht, bevor sie da ist, liest weiter aus dem Netz.
 */
export async function inDenRam(url: string, gebrauchen = true): Promise<string | null> {
  if (typeof window === 'undefined' || typeof fetch !== 'function') return null;
  const da = vorrat.get(url);
  if (da) {
    da.zuletzt = Date.now();
    if (gebrauchen) gebraucht.set(url, da.zuletzt);
    return da.blob;
  }
  const nein = geht_nicht.get(url);
  if (nein !== undefined) {
    if (Date.now() - nein < NEUER_VERSUCH_NACH) return null;
    geht_nicht.delete(url);
  }
  const schon = laufend.get(url);
  if (schon) return schon;
  // Wer beim Leeren dabei war, wird danach nicht mehr eingetragen: sonst
  // legt ein Holen, das beim Verlassen des Projekts noch unterwegs war, eine
  // Blob-Adresse ab, die niemand mehr freigibt.
  const stand = leerungen;

  const holen = (async (): Promise<string | null> => {
    try {
      // Ohne `no-store`: der Service Worker soll antworten dürfen, wenn er die
      // Datei schon hat (7.69n). Genau dann kostet das hier gar nichts mehr.
      const antwort = await fetch(url);
      if (!antwort.ok) {
        geht_nicht.set(url, Date.now());
        return null;
      }
      // Erst die angekündigte Länge, dann der Inhalt: sonst zöge ein grosser
      // Brocken erst vierhundert Megabyte in den Speicher, und die Regel
      // würde ihn danach ablehnen. Steht keine Länge da, bleibt nur der
      // zweite Weg -- die Regel fängt es dann nach dem Laden ab.
      const laenge = Number(antwort.headers.get('content-length') ?? 0);
      if (laenge > RAM_JE_DATEI) {
        await antwort.body?.cancel();
        geht_nicht.set(url, Date.now());
        return null;
      }
      const blob = await antwort.blob();
      if (stand !== leerungen) return null;
      const plan = ramPlan(eintraege(), { schluessel: url, bytes: blob.size }, inGebrauch());
      if (!plan.legen) {
        // „Schon da“ ist kein Fehlschlag, alles andere schon.
        if (plan.grund !== 'schon-da') geht_nicht.set(url, Date.now());
        return plan.grund === 'schon-da' ? (vorrat.get(url)?.blob ?? null) : null;
      }
      for (const raus of plan.werfen) {
        const alt = vorrat.get(raus);
        if (!alt) continue;
        URL.revokeObjectURL(alt.blob);
        vorrat.delete(raus);
      }
      const adresse = URL.createObjectURL(blob);
      vorrat.set(url, { bytes: blob.size, zuletzt: Date.now(), blob: adresse });
      if (gebrauchen) gebraucht.set(url, Date.now());
      return adresse;
    } catch {
      // Kein Netz, keine Erlaubnis, kein Speicher: dann bleibt es beim alten
      // Weg. Schlechter als vorher wird es dadurch nie -- aber gemerkt wird
      // es, damit nicht jedes Bild neu fragt.
      geht_nicht.set(url, Date.now());
      return null;
    } finally {
      laufend.delete(url);
    }
  })();
  laufend.set(url, holen);
  return holen;
}

export interface RamStand {
  dateien: number;
  bytes: number;
  inGebrauch: number;
  laufend: number;
}

export function ramStand(): RamStand {
  return {
    dateien: vorrat.size,
    bytes: [...vorrat.values()].reduce((s, e) => s + e.bytes, 0),
    inGebrauch: inGebrauch().size,
    laufend: laufend.size,
  };
}

/** Beim Verlassen eines Projekts: alles freigeben. */
export function ramLeeren(): void {
  leerungen += 1;
  for (const s of vorrat.values()) URL.revokeObjectURL(s.blob);
  vorrat.clear();
  gebraucht.clear();
  geht_nicht.clear();
  laufend.clear();
}

declare global {
  interface Window {
    __ram?: () => string;
  }
}
if (typeof window !== 'undefined') window.__ram = () => JSON.stringify(ramStand());
